import WinstonTransport from 'winston-transport';
import { BetterStackLogger, SEVERITY, LEVEL_NAMES } from '@testim/shared-logger';
import type { LogEntry } from '@testim/shared-logger';
import type { LogEntry as WinstonLogEntry } from 'winston';

export interface BetterStackTransportOptions extends WinstonTransport.TransportStreamOptions {
    ingestUrl: string;
    token: string;
}

const severityMap = {
    debug: SEVERITY.debug,
    silly: SEVERITY.verbose,
    verbose: SEVERITY.verbose,
    silent: SEVERITY.verbose,
    info: SEVERITY.info,
    http: SEVERITY.info,
    warn: SEVERITY.warn,
    warning: SEVERITY.warn,
    error: SEVERITY.error,
    critical: SEVERITY.fatal,
    crit: SEVERITY.fatal,
} as const;

const errorProps = ['err', 'error', 'reason', 'e'];

/**
 * Winston transport adapter for Better Stack.
 * 
 * ARCHITECTURE: This is a thin adapter that:
 * 1. Receives log entries from Winston
 * 2. Converts Winston log format to Better Stack LogEntry format
 * 3. Delegates to shared BetterStackLogger for buffering and sending
 * 
 * Uses superagent with proxy support (auto-selected by shared logger in Node.js).
 */
export class BetterStackWinstonTransport extends WinstonTransport {
    name: string;
    private readonly logger: BetterStackLogger;
    private proxy: string | undefined;

    constructor(options: BetterStackTransportOptions) {
        super(options);
        this.name = 'BetterStack Transport';

        this.logger = new BetterStackLogger({
            ingestUrl: options.ingestUrl,
            token: options.token,
            getProxy: () => this.proxy,
        });
    }

    log(info: WinstonLogEntry, callback: (...args: unknown[]) => void): void {
        const { category, level, message: msg, meta: infoMeta = {}, ...restMeta } = info;
        const meta = { ...infoMeta, ...restMeta };

        const severity = severityMap[level as keyof typeof severityMap] ?? SEVERITY.info;

        const log: LogEntry = {
            dt: new Date().toISOString(),
            level: LEVEL_NAMES[severity],
            severity,
            message: msg,
            category: category || 'ROOT',
            applicationName: 'testim',
            subsystemName: 'runner',
        };

        if (meta.className) log.className = meta.className;
        if (meta.methodName) log.methodName = meta.methodName;
        if (meta.threadId) log.threadId = meta.threadId;

        delete meta.className;
        delete meta.methodName;
        delete meta.threadId;
        delete meta.category;
        delete meta.level;
        delete meta.message;

        let errorOverride = false;
        if (infoMeta instanceof Error) {
            errorOverride = true;
            meta.msg = infoMeta.message + infoMeta.stack;
            if (msg) meta.msg = `${msg}\n${meta.msg}`;
        }

        for (const prop of errorProps) {
            if (infoMeta[prop] instanceof Error) {
                meta[prop] = {
                    message: infoMeta[prop].message,
                    stack: infoMeta[prop].stack,
                    name: infoMeta[prop].name,
                    type: (infoMeta[prop] as any).type,
                    cause: infoMeta[prop].cause,
                    ...(infoMeta[prop] as any),
                };
            }
        }

        if (Object.keys(meta).length > 0) {
            if (msg && !errorOverride) meta.msg = msg;
            Object.assign(log, meta);
        }

        this.logger.addLog(log);
        callback(null, true);
    }

    setProxy(proxy: string): void {
        this.proxy = proxy;
    }

    async waitForFlush(): Promise<void> {
        return await this.logger.waitForFlush();
    }
}
